import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, CornerDownLeft } from 'lucide-react';
import VolumeControl from './VolumeControl';
import VideoPlayPauseButton from './VideoPlayPauseButton';
import { getCloudflarePosterUrl } from '../utils/cloudflareStream';
import useScrollAutoplay from '../hooks/useScrollAutoplay';

/**
 * HeroControlsPill — opt-in (useBottomLeftControls) combined play/pause +
 * mute/volume control used only by the Home page hero. Frosted-glass pill,
 * fully invisible by default; visibility is driven by the caller via
 * wrapper classes/opacity (hover on desktop, tap-timer on mobile).
 */
const HeroControlsPill = ({ isPlaying, onTogglePlay, videoRef, isMuted, onToggleMute, isMobile }) => {
  const btnSize = isMobile ? 'w-7 h-7' : 'w-8 h-8';
  const iconSize = isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <div
      className="flex items-center gap-0.5 rounded-full bg-black/60 backdrop-blur-sm"
      style={{ padding: isMobile ? 4 : 5 }}
      data-testid="hero-controls-pill"
    >
      <button
        type="button"
        onClick={onTogglePlay}
        className={`${btnSize} rounded-full flex items-center justify-center hover:bg-white/15 transition-colors duration-200`}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        data-testid="hero-pill-play-button"
      >
        {isPlaying ? <Pause className={`${iconSize} text-white`} /> : <Play className={`${iconSize} text-white`} />}
      </button>
      <VolumeControl
        videoRef={videoRef}
        isMuted={isMuted}
        onToggleMute={onToggleMute}
        buttonClassName={`${btnSize} rounded-full flex items-center justify-center hover:bg-white/15 transition-colors duration-200 group`}
        iconClassName={`${iconSize} text-white group-hover:scale-110 transition-transform`}
        motionDelay={0}
        testId="hero-pill-volume-button"
      />
    </div>
  );
};

/**
 * HeroTypingPill — opt-in (showTypingPill) frosted-glass "subtitle" pill
 * that slides up + fades in once the video reaches `typingPillTriggerSeconds`,
 * then types `text` out one character at a time (driven by `typedLength`
 * from the parent). Gold dot on the left, blinking gold cursor, and a
 * gold ↵ icon on the right that triggers `onBookDemo`.
 */
const HeroTypingPill = ({ text, typedLength, visible, onBookDemo, isMobile }) => (
  <div
    className="flex items-center rounded-full bg-black/55 backdrop-blur-md"
    style={{
      gap: isMobile ? 4 : 10,
      padding: isMobile ? '7px 11px' : '12px 22px',
      border: '1px solid rgba(255,255,255,0.12)',
      maxWidth: isMobile ? '100%' : 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      transform: visible ? 'translateY(0)' : 'translateY(16px)',
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'auto' : 'none',
      transition: 'transform 1.3s cubic-bezier(0.22,1,0.36,1), opacity 1.3s ease-out',
    }}
    data-testid="hero-typing-pill"
  >
    <span
      className="rounded-full flex-shrink-0"
      style={{ width: isMobile ? 4 : 6, height: isMobile ? 4 : 6, background: '#D4AF37' }}
      aria-hidden="true"
    />
    <span
      className="font-bold text-white"
      style={{ fontSize: isMobile ? 11 : 17, letterSpacing: '0.01em' }}
      data-testid="hero-typing-pill-text"
    >
      {text.slice(0, typedLength)}
    </span>
    <span
      className="hologram-cursor-blink flex-shrink-0"
      style={{ width: 2, height: isMobile ? 11 : 16, background: '#D4AF37' }}
      aria-hidden="true"
      data-testid="hero-typing-pill-cursor"
    />
    <button
      type="button"
      onClick={onBookDemo}
      className="flex-shrink-0 flex items-center justify-center rounded-full hover:bg-white/15 transition-colors duration-200"
      style={{ width: isMobile ? 17 : 26, height: isMobile ? 17 : 26, marginLeft: isMobile ? 1 : 4 }}
      aria-label="Book a demo"
      data-testid="hero-typing-pill-book-demo-button"
    >
      <CornerDownLeft style={{ width: isMobile ? 10 : 14, height: isMobile ? 10 : 14, color: '#D4AF37' }} />
    </button>
  </div>
);

/**
 * Flexible Hero Media Component
 * Supports background video (YouTube embed, uploaded MP4, Cloudflare Stream), and images
 * 
 * Usage:
 * For YOUTUBE VIDEO:      <HeroMedia type="video" videoId="YOUR_YOUTUBE_ID" />
 * For UPLOADED VIDEO:     <HeroMedia type="uploaded-video" videoUrl="YOUR_VIDEO_URL" />
 * For CLOUDFLARE STREAM:  <HeroMedia type="cloudflare-stream" cloudflareVideoId="YOUR_VIDEO_ID" />
 * For IMAGE:              <HeroMedia type="image" imageUrl="YOUR_IMAGE_URL" />
 *
 * NOTE — Cloudflare Stream customer subdomain is fixed to the GoogLyn AI
 * account (customer-q87c1i5e2o40bvjt). To swap in the production hero clip,
 * upload it to Cloudflare Stream and update the `cloudflareVideoId` in the
 * page's heroSettings — no other changes needed.
 */
const HeroMedia = ({ 
  type = 'image', // 'video', 'uploaded-video', 'cloudflare-stream', or 'image'
  videoId = '', 
  videoUrl = '',
  cloudflareVideoId = '',
  imageUrl = '',
  overlay = 0.3,
  // Opt-in only — adds a play/pause control (identical to the uploaded-video
  // one) to a cloudflare-stream hero. Defaults to false so every other
  // cloudflare-stream HeroMedia usage on the site is completely unaffected.
  showPlayPauseControl = false,
  // Opt-in only (Home page hero) — replaces the centered play button + the
  // bottom-right volume button with one combined bottom-left frosted-glass
  // pill (hover-to-reveal on desktop, tap-to-reveal for 2.5s on mobile).
  // Clicking/tapping anywhere on the video also toggles play/pause.
  useBottomLeftControls = false,
  // Opt-in only (Home page hero) — "subtitle" pill that slides up + types
  // out `typingPillText` once the video reaches `typingPillTriggerSeconds`,
  // and resets when the video loops back to the start.
  showTypingPill = false,
  typingPillText = '',
  typingPillTriggerSeconds = 0,
  onBookDemo = () => {},
  children 
}) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [pillVisible, setPillVisible] = useState(false);
  const [typedLength, setTypedLength] = useState(0);
  const [mobileControlsVisible, setMobileControlsVisible] = useState(false);
  const videoRef = useRef(null);
  const streamIframeRef = useRef(null);
  const streamPlayerRef = useRef(null);
  const pillTriggeredRef = useRef(false);
  const typingTimerRef = useRef(null);
  const mobileHideTimerRef = useRef(null);

  // Only play the video while it is scrolled into view — pauses it off-screen.
  useScrollAutoplay(videoRef, isPlaying, 0.25, type === 'uploaded-video' && !!videoUrl);

  // Drives the typing pill: slides/fades in once the video crosses the
  // trigger second, types the text out one character at a time, then
  // resets (hides) once the video loops back near the start.
  useEffect(() => {
    if (!showTypingPill || type !== 'uploaded-video' || !videoRef.current) return undefined;
    const video = videoRef.current;

    const startTyping = () => {
      setTypedLength(0);
      let i = 0;
      typingTimerRef.current = setInterval(() => {
        i += 1;
        setTypedLength(i);
        if (i >= typingPillText.length) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }, 80);
    };

    const resetPill = () => {
      pillTriggeredRef.current = false;
      setPillVisible(false);
      setTypedLength(0);
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };

    const handleTimeUpdate = () => {
      const t = video.currentTime;
      if (!pillTriggeredRef.current && t >= typingPillTriggerSeconds) {
        pillTriggeredRef.current = true;
        setPillVisible(true);
        startTyping();
      } else if (pillTriggeredRef.current && t < Math.max(0, typingPillTriggerSeconds - 2)) {
        // Video looped back to the start — fade out and allow re-trigger
        resetPill();
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTypingPill, type, typingPillTriggerSeconds, typingPillText]);

  useEffect(() => () => {
    if (mobileHideTimerRef.current) clearTimeout(mobileHideTimerRef.current);
  }, []);

  // Keep isPlaying in sync with the actual <video> playback state, so the
  // pill's icon (and any button toggling off that state) stays correct even
  // if autoplay gets rejected by the browser or the video pauses on its own.
  useEffect(() => {
    if (type !== 'uploaded-video' || !videoRef.current) return undefined;
    const video = videoRef.current;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [type]);

  // Mobile (no hover): tapping the video reveals the controls pill for ~2.5s
  const pokeMobileControls = () => {
    setMobileControlsVisible(true);
    if (mobileHideTimerRef.current) clearTimeout(mobileHideTimerRef.current);
    mobileHideTimerRef.current = setTimeout(() => setMobileControlsVisible(false), 2500);
  };

  // Loads the Cloudflare Stream Player SDK and wires it to the iframe so the
  // play/pause control below can toggle playback via the player API.
  useEffect(() => {
    if (type !== 'cloudflare-stream' || !cloudflareVideoId || !showPlayPauseControl) return;

    const initPlayer = () => {
      if (streamIframeRef.current && window.Stream) {
        streamPlayerRef.current = window.Stream(streamIframeRef.current);
      }
    };

    if (window.Stream) {
      initPlayer();
      return;
    }

    let script = document.querySelector('script[data-cloudflare-stream-sdk]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
      script.setAttribute('data-cloudflare-stream-sdk', 'true');
      document.body.appendChild(script);
    }
    script.addEventListener('load', initPlayer);
    return () => script.removeEventListener('load', initPlayer);
  }, [type, cloudflareVideoId, showPlayPauseControl]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const togglePlayPause = () => {
    if (type === 'cloudflare-stream') {
      if (!streamPlayerRef.current) return;
      if (isPlaying) {
        streamPlayerRef.current.pause();
      } else {
        streamPlayerRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
      return;
    }
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="group/video relative min-h-[85vh] overflow-hidden">
      {/* Background Media */}
      {type === 'cloudflare-stream' && cloudflareVideoId ? (
        // Cloudflare Stream embed — hero background. Container is absolute
        // and full-bleed; the iframe is centered and scaled so that its
        // 16:9 aspect always covers the section regardless of viewport
        // dimensions (177.78vh wide OR 56.25vw tall, whichever is larger,
        // via the min-width/min-height 100% trick).
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 0,
          }}
          data-testid="hero-cloudflare-stream-wrapper"
        >
          <iframe
            ref={streamIframeRef}
            title="Hero Background Video"
            src={`https://customer-q87c1i5e2o40bvjt.cloudflarestream.com/${cloudflareVideoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-q87c1i5e2o40bvjt.cloudflarestream.com%2F${cloudflareVideoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false`}
            style={{
              border: 'none',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '177.78vh',
              minWidth: '100%',
              height: '56.25vw',
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
            }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
          />

          {/* Centered play/pause overlay — opt-in only (showPlayPauseControl),
              identical styling/behavior to the uploaded-video hero button. */}
          {showPlayPauseControl && (
            <VideoPlayPauseButton
              isPlaying={isPlaying}
              onClick={togglePlayPause}
              testId="hero-play-button"
            />
          )}
        </div>
      ) : type === 'uploaded-video' && videoUrl ? (
        <>
          <video
            ref={videoRef}
            loop
            muted={isMuted}
            playsInline
            preload="metadata"
            poster={getCloudflarePosterUrl(videoUrl)}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-uploaded-video"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>

          {useBottomLeftControls ? (
            <>
              {/* Transparent click/tap layer above the hero's children
                  wrapper (z-10) so a tap/click anywhere on the video toggles
                  play/pause and (mobile) reveals the controls pill. Sits
                  below the pills (z-20) so their own buttons stay clickable. */}
              <div
                className="absolute inset-0 z-[15] cursor-pointer"
                onClick={() => { togglePlayPause(); pokeMobileControls(); }}
                data-testid="hero-video-click-overlay"
              />
              {/* Desktop (md+): controls pill bottom-left (aligned with the
                  nav logo via the same mx-auto max-w-7xl container), typing
                  pill centered, both on the same line. Controls invisible
                  by default, fade in on hover over the video. */}
              <div
                className="hidden md:flex absolute inset-x-0 z-20 items-center pointer-events-none"
                style={{ bottom: '2rem' }}
              >
                <div
                  className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center"
                  style={{ minHeight: 44 }}
                >
                  <div className={`transition-opacity duration-300 group-hover/video:opacity-100 group-hover/video:pointer-events-auto ${mobileControlsVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                    <HeroControlsPill
                      isPlaying={isPlaying}
                      onTogglePlay={togglePlayPause}
                      videoRef={videoRef}
                      isMuted={isMuted}
                      onToggleMute={toggleMute}
                      isMobile={false}
                    />
                  </div>
                  {showTypingPill && typingPillText && (
                    <div className="absolute left-1/2 -translate-x-1/2">
                      <HeroTypingPill
                        text={typingPillText}
                        typedLength={typedLength}
                        visible={pillVisible}
                        onBookDemo={onBookDemo}
                        isMobile={false}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile (<md): typing pill, controls pill directly below it,
                  both left-aligned to each other as a centered group.
                  Controls appear only for ~2.5s after a tap on the video. */}
              <div className="flex md:hidden absolute inset-x-0 bottom-6 justify-center px-4 z-20 pointer-events-none">
                <div className="flex flex-col items-start gap-2" style={{ maxWidth: 'calc(100vw - 32px)' }}>
                  {showTypingPill && typingPillText && (
                    <div className="pointer-events-auto" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      <HeroTypingPill
                        text={typingPillText}
                        typedLength={typedLength}
                        visible={pillVisible}
                        onBookDemo={onBookDemo}
                        isMobile
                      />
                    </div>
                  )}
                  <div
                    className={`transition-opacity duration-300 ${mobileControlsVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                  >
                    <HeroControlsPill
                      isPlaying={isPlaying}
                      onTogglePlay={() => { togglePlayPause(); pokeMobileControls(); }}
                      videoRef={videoRef}
                      isMuted={isMuted}
                      onToggleMute={() => { toggleMute(); pokeMobileControls(); }}
                      isMobile
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Centered play/pause overlay (hover-only). Volume/mute stays bottom-right. */}
              <VideoPlayPauseButton
                isPlaying={isPlaying}
                onClick={togglePlayPause}
                testId="hero-play-button"
              />

              {/* Video Control Buttons - Bottom Right */}
              <div className="absolute bottom-6 right-6 z-20 flex items-center gap-3">
                {/* Mute/Unmute Toggle Button with Hover Volume Slider */}
                <VolumeControl
                  videoRef={videoRef}
                  isMuted={isMuted}
                  onToggleMute={toggleMute}
                  testId="hero-volume-button"
                />
              </div>
            </>
          )}
        </>
      ) : type === 'video' && videoId ? (
        <div className="absolute inset-0 w-full h-full">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=${videoId}`}
            title="Hero Background Video"
            allow="autoplay; encrypted-media"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              width: '100vw',
              height: '100vh',
              minWidth: '100%',
              minHeight: '100%'
            }}
          />
        </div>
      ) : (
        <div className="absolute inset-0 w-full h-full">
          <img
            src={imageUrl || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80'}
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Gradient Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(232, 160, 184, ${overlay * 0.3}), rgba(244, 194, 212, ${overlay * 0.2})), linear-gradient(to bottom, rgba(0, 0, 0, ${overlay}), rgba(0, 0, 0, ${overlay * 0.7}))`
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full">
        {children}
      </div>
    </div>
  );
};

export default HeroMedia;
